/**
 * Type guard and interface for graph preview data.
 * Used to detect when tool results contain graph/chart data that should auto-expand.
 */

export interface GraphPreviewData {
  title: string;
  type: string;
  product_type: string;
  query?: string;
  expressions: Array<{ aggregator: string; column: string }>;
  group_by?: string[];
  display?: string;
  metrics: {
    bucket_count: number;
    sample_factor: number;
    buckets: Array<{
      bucket_id: number;
      bucket_min?: number | null;
      bucket_max?: number | null;
      bucket_value?: number | null;
      group: string[];
      column: string;
      metric_type: string;
      metric_value?: number | null;
    }>;
  };
}

export const isGraphPreviewData = (data: unknown): data is GraphPreviewData => {
  const d = data as GraphPreviewData;
  return (
    d != null &&
    typeof d.title === 'string' &&
    typeof d.type === 'string' &&
    typeof d.product_type === 'string' &&
    Array.isArray(d.expressions) &&
    d.metrics != null &&
    Array.isArray(d.metrics.buckets)
  );
};

/**
 * Extract the output string from a tool result. Accepts either a plain string
 * or an envelope shaped like `{ content: [{ text: '...' }] }`. Returns '' when
 * no string text can be recovered — callers can then `.trim()` to check for
 * meaningful output.
 */
export function getToolOutputString(data: unknown): string {
  if (data == null) {
    return '';
  }
  if (typeof data === 'string') {
    return data;
  }
  const d = data as { content?: Array<{ text?: string }> };
  const text = d?.content?.[0]?.text;
  return typeof text === 'string' ? text : '';
}

/** Try to parse tool response data, unwrapping the content[0].text envelope if present. */
export function tryParseToolData(data: unknown): unknown {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    const inner = (parsed as { content?: Array<{ text?: string }> } | null)?.content?.[0]?.text;
    return inner ? JSON.parse(inner) : parsed;
  } catch {
    return data;
  }
}
